import {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useId,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { subscribeVisibleViewport, visibleViewportBand } from "@njbsoft/form-ui";
import { inputClass } from "./form-field";

interface ComboboxOption {
  value: string;
  label: string;
  /**
   * Listed but not choosable: it stays in the list (and in the keyboard walk)
   * so the reader learns it exists and why it is refused, but a click or Enter
   * on it selects nothing.
   */
  disabled?: boolean;
  /** Why a disabled option is refused, drawn under its label. */
  reason?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  value: string;
  onChange: (value: string) => void;
  onSearch?: (query: string) => void;
  loading?: boolean;
  placeholder?: string;
  className?: string;
  /** Override the displayed label for the selected value when it may not be in the options list */
  selectedLabel?: string;
  /** Locks the input (no focus/type/open) and hides the clear button. Default false. */
  disabled?: boolean;
  /** id for the text input, so a sibling `<label htmlFor>` can name it. */
  id?: string;
  /**
   * Accessible name for a control no visible label wraps or points at — a
   * filter-bar combobox whose only visible text is its placeholder.
   */
  ariaLabel?: string;
  /**
   * Announces the control as invalid (`aria-invalid`). The visual error edge
   * rides `inputClassName` and was already reaching this control; this is the
   * half assistive tech reads, and without it the field most likely to be
   * errored was the one screen-reader users were not told about.
   */
  invalid?: boolean;
  /**
   * id of the element describing the control — an error paragraph, typically.
   * Caller-supplied rather than derived from `id`: the app `FormField` gives its
   * error `<p>` no id at all while the form namespace names its own
   * `{htmlFor}-error`, so no single suffix rule fits both hosts.
   */
  describedBy?: string;
  /** Replaces the default app inputClass on the text input — form-document
   * mounts pass the form control chrome so the search input sits on form
   * tokens in both light and dark. */
  inputClassName?: string;
}

function optionTone(option: ComboboxOption, active: boolean): string {
  if (option.disabled === true) {
    return `cursor-not-allowed text-muted ${active ? "bg-gray-50 dark:bg-gray-700" : ""}`;
  }
  return active
    ? "cursor-pointer bg-primary/10 text-primary"
    : "cursor-pointer text-on-surface hover:bg-gray-50 dark:hover:bg-gray-700";
}

/** The listbox's resting height, and the floor it is never squeezed under. */
const LIST_MAX_HEIGHT = 240;
const LIST_MIN_HEIGHT = 96;

/** Breathing room between the input and the list. */
const LIST_GAP = 4;

/**
 * The clear control: the bare glyph at the input's right edge from `sm`; below
 * `sm` a 44x44 target over the input's right end (the input is 44px tall
 * there), with the input's text stopping short of it, so a tap at the end of a
 * long label places the caret instead of clearing the choice.
 */
const CLEAR_BUTTON_CLASS =
  "absolute right-2 top-1/2 -translate-y-1/2 text-muted hover:text-on-surface max-sm:right-0 max-sm:flex max-sm:size-11 max-sm:items-center max-sm:justify-center";
const CLEARABLE_INPUT_CLASS = "max-sm:pr-11";

/**
 * The listbox's resolved position. It is anchored by its TOP under the input,
 * or — when the input sits low enough that a full list would run past the fold —
 * by its BOTTOM above the input. A fixed overlay cannot be scrolled into view,
 * so a list opened past the bottom edge is unreachable rather than merely
 * awkward: every option under the fold is unclickable, and the control looks
 * like it simply refuses to answer.
 */
interface Rect {
  top?: number;
  bottom?: number;
  left: number;
  width: number;
  maxHeight: number;
}

export function Combobox({
  options,
  value,
  onChange,
  onSearch,
  loading = false,
  placeholder = "",
  className = "",
  selectedLabel: selectedLabelProp,
  disabled = false,
  inputClassName,
  id,
  ariaLabel,
  invalid = false,
  describedBy,
}: ComboboxProps) {
  const { t } = useTranslation("common");
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const [query, setQuery] = useState("");
  /**
   * Whether `query` is text the USER typed. The input is ALSO seeded from the
   * current selection's label — on focus, on select, on Escape — so the box
   * reads as the selection instead of going blank. A label is not a search
   * term, and forwarding one asks the server for UI text: a picker whose label
   * carries any decoration ("Riverton — premise"), or whose default selection
   * names no row at all ("Sample data"), searched for that string and opened on
   * an empty list, with the record just picked missing from its own results.
   * Seeded queries therefore search for NOTHING, which is the full list.
   */
  const [userTyped, setUserTyped] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [rect, setRect] = useState<Rect | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = selectedLabelProp ?? options.find((o) => o.value === value)?.label ?? "";
  const clearable = value !== "" && !disabled;

  const filtered = onSearch
    ? options
    : query
      ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
      : options;

  // Debounced server-side search
  useEffect(() => {
    if (!onSearch) return;
    const term = userTyped ? query : "";
    const timer = setTimeout(() => onSearch(term), 300);
    return () => clearTimeout(timer);
  }, [query, userTyped, onSearch]);

  // A control that goes disabled while its listbox is open force-closes it.
  // `showListbox` already hides the list, but `isOpen` drove `aria-expanded`
  // independently, so the input announced an expanded popup that no longer
  // existed — and un-disabling would spring the list back open on a control
  // the user has since stopped interacting with. Clearing the state, rather
  // than gating the attribute, closes both.
  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  // Measure the input so the portaled listbox can position itself as a fixed
  // overlay against it, flipping above when the visible band (above the soft
  // keyboard) has no room below. Recompute on open.
  const measure = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const band = visibleViewportBand();
    const below = band.bottom - r.bottom - LIST_GAP;
    const above = r.top - band.top - LIST_GAP;
    const flip = below < LIST_MAX_HEIGHT && above > below;
    const room = Math.max(LIST_MIN_HEIGHT, Math.min(LIST_MAX_HEIGHT, flip ? above : below));
    setRect(
      flip
        ? {
            bottom: window.innerHeight - r.top + LIST_GAP,
            left: r.left,
            width: r.width,
            maxHeight: room,
          }
        : { top: r.bottom + LIST_GAP, left: r.left, width: r.width, maxHeight: room },
    );
  }, []);

  useLayoutEffect(() => {
    if (isOpen) measure();
  }, [isOpen, measure]);

  // Keep the overlay glued to the input while the dialog/page scrolls or the
  // viewport resizes or the soft keyboard moves the visible band. `capture:
  // true` catches scroll on the dialog's inner overflow box (scroll does not
  // bubble).
  useEffect(() => {
    if (!isOpen) return;
    const handler = () => measure();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler, true);
    const unsubscribeViewport = subscribeVisibleViewport(handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler, true);
      unsubscribeViewport();
    };
  }, [isOpen, measure]);

  // Close on outside click. The portaled listbox lives OUTSIDE containerRef, so
  // clicks inside it would otherwise read as "outside" and close before the
  // option's onMouseDown fires — treat the listbox as inside too.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      const inContainer = containerRef.current?.contains(target) ?? false;
      const inListbox = listboxRef.current?.contains(target) ?? false;
      if (!inContainer && !inListbox) {
        setIsOpen(false);
        if (!value) {
          setQuery("");
          setUserTyped(false);
        }
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [value]);

  // Reset active index when filtered list changes
  useEffect(() => {
    setActiveIndex(-1);
  }, [filtered.length]);

  const handleSelect = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      const label = options.find((o) => o.value === optionValue)?.label ?? "";
      setQuery(label);
      setUserTyped(false);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange, options],
  );

  const handleClear = useCallback(() => {
    onChange("");
    setQuery("");
    setUserTyped(false);
    setIsOpen(false);
  }, [onChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
      setIsOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const active = activeIndex >= 0 ? filtered[activeIndex] : undefined;
      if (active !== undefined && active.disabled !== true) handleSelect(active.value);
    } else if (e.key === "Escape") {
      // Only an OPEN listbox has a popup to dismiss, and only then is the
      // Escape ours to consume. `preventDefault` marks it handled so an
      // enclosing dialog or drawer does not close on the same press — see
      // `FormDrawer`'s one-press-one-layer contract.
      if (!isOpen) return;
      e.preventDefault();
      setIsOpen(false);
      setQuery(selectedLabel);
      setUserTyped(false);
    }
  };

  // Scroll active option into view
  useEffect(() => {
    if (activeIndex >= 0 && listboxRef.current) {
      const item = listboxRef.current.children[activeIndex] as HTMLElement | undefined;
      item?.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const activeId =
    activeIndex >= 0 && filtered[activeIndex]
      ? `${baseId}-opt-${filtered[activeIndex].value}`
      : undefined;

  const showListbox =
    !disabled && isOpen && (filtered.length > 0 || loading || onSearch) && rect !== null;

  const listbox = showListbox ? (
    <ul
      ref={listboxRef}
      id={listboxId}
      role="listbox"
      style={{
        top: rect.top,
        bottom: rect.bottom,
        left: rect.left,
        width: rect.width,
        maxHeight: rect.maxHeight,
      }}
      className="fixed z-[60] overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800"
    >
      {filtered.map((opt, idx) => (
        <li
          key={opt.value}
          id={`${baseId}-opt-${opt.value}`}
          role="option"
          aria-selected={idx === activeIndex}
          aria-disabled={opt.disabled === true || undefined}
          onMouseDown={(e) => {
            e.preventDefault();
            if (opt.disabled !== true) handleSelect(opt.value);
          }}
          onMouseEnter={() => setActiveIndex(idx)}
          className={`px-3 py-2 text-sm max-sm:min-h-11 max-sm:py-3 ${optionTone(
            opt,
            idx === activeIndex,
          )} ${opt.value === value ? "font-medium" : ""}`}
        >
          {opt.label}
          {opt.reason !== undefined && (
            <span className="mt-0.5 block text-xs text-muted">{opt.reason}</span>
          )}
        </li>
      ))}
      {loading && filtered.length === 0 && (
        <li className="px-3 py-2 text-sm text-muted" aria-disabled="true">
          {t("components.combobox.searching")}
        </li>
      )}
      {!loading && filtered.length === 0 && query && (
        <li className="px-3 py-2 text-sm text-muted" aria-disabled="true">
          {t("components.combobox.noResults")}
        </li>
      )}
      {!loading && filtered.length === 0 && !query && onSearch && (
        <li className="px-3 py-2 text-sm text-muted" aria-disabled="true">
          {t("components.combobox.typeToSearch")}
        </li>
      )}
    </ul>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className} ${disabled ? "opacity-60" : ""}`}>
      <input
        ref={inputRef}
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-activedescendant={activeId}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        className={`${inputClassName ?? inputClass}${clearable ? ` ${CLEARABLE_INPUT_CLASS}` : ""}`}
        placeholder={placeholder}
        value={isOpen ? query : selectedLabel || query}
        onFocus={(e) => {
          if (disabled) return;
          setIsOpen(true);
          setQuery(selectedLabel);
          setUserTyped(false);
          // The seeded text is the SELECTION, not a search the user started, so
          // typing has to replace it rather than splice into it — a caret
          // dropped mid-label turned the first keystroke into gibberish nobody
          // could match.
          e.currentTarget.select();
        }}
        onChange={(e) => {
          if (disabled) return;
          setQuery(e.target.value);
          setUserTyped(true);
          setIsOpen(true);
        }}
        onKeyDown={disabled ? undefined : handleKeyDown}
      />
      {clearable && (
        <button
          type="button"
          onClick={handleClear}
          className={CLEAR_BUTTON_CLASS}
          aria-label={t("components.combobox.clearSelection")}
        >
          &times;
        </button>
      )}
      {listbox && createPortal(listbox, document.body)}
    </div>
  );
}
